import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../contexts/AuthContext'
import { brl } from './finance-shared'
import { SideModal } from './SideModal'
import { FiRefreshCw, FiAlertTriangle, FiCheckCircle, FiChevronRight, FiLoader, FiList, FiTrash2, FiExternalLink, FiEyeOff, FiEye } from 'react-icons/fi'

// Apontamentos do setor — a coluna de pendências ao lado dos cards de resumo
// em Contas a Pagar.
//
// A ideia: o pente-fino que chega no Telegram às 06:00 dizia o que havia de
// errado, mas quem ia consertar estava em OUTRA tela, e o número já era de
// horas atrás. Aqui o mesmo diagnóstico fica ao lado dos valores, com o botão
// que leva direto ao lugar do conserto e um recarregar que reconfere na hora —
// resolveu na mão, clica, o apontamento some. É esse ciclo que importa.
//
// O backend (routes/apontamentos.cjs) usa o critério IDÊNTICO ao do script do
// Telegram, de propósito: tela e PDF têm que dizer o mesmo número.
//
// DENSIDADE (revisão 27/07): tudo aqui é deliberadamente apertado — 11px,
// padding de 8px, uma linha por informação. Este bloco divide a dobra com a
// tabela de lançamentos, que é o conteúdo principal da tela; cada pixel que ele
// toma é uma linha de lançamento que some. Ao mexer, resista a "dar um respiro".

export interface Apontamento {
  key: string
  alerta: boolean
  titulo: string
  detalhe: string
  valor: number | null
  acao: string | null
  // Por que isto é um apontamento + o que fazer. Vem do backend, ao lado do
  // critério — se o texto morasse aqui, divergiria do SQL na primeira mudança.
  explicacao?: string | null
  // O backend tem gaveta de detalhe para esta chave? Opcional para não quebrar
  // com resposta antiga; `undefined` é tratado como sim.
  detalhavel?: boolean
}

// Payload do onAcao: `status` diz em qual aba da conciliação o movimento vive.
// Sem ele o foco cai sempre em 'pendente' — e um conciliado-sem-vínculo é
// 'conciliado', ficaria focado numa aba onde a linha não existe.
export interface AcaoPayload { movimentoId?: number; status?: 'pendente' | 'conciliado' }

export interface MesConciliacao {
  mes: number
  total: number        // movimentos do extrato importados nesse mês
  conciliados: number
  ignorados: number    // linha de lote cujos títulos já estão lançados — decisão final
  tratados: number     // conciliados + ignorados: o que NÃO está mais na fila
  pagos: number        // lançamentos pagos no mês — a régua de "teve vida"
  temExtrato: boolean
  pct: number | null
  estado: 'ok' | 'incompleto' | 'parcial' | 'sem_extrato' | 'vazio' | 'futuro'
}

export interface ApontamentosData {
  checkedAt: string
  pendentes: number
  itens: Apontamento[]
  conciliacao: { year: number; meses: MesConciliacao[] } | null
}

// Um fetch só alimenta os dois pedaços (coluna + faixa), que vivem em pontos
// diferentes da árvore — daí o hook em vez de estado interno de cada um.
//
// `enabled` existe porque Contas.tsx é a MESMA tela de Contas a Receber, e lá a
// coluna não aparece — hook não pode ser condicional, então quem desliga é o
// parâmetro, e não um `if` em volta da chamada.
export function useApontamentos(kind: string, year: number, enabled = true) {
  const { authenticatedFetch } = useAuth()
  const [data, setData] = useState<ApontamentosData | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [erro, setErro] = useState<string | null>(null)

  const recarregar = useCallback(async () => {
    if (!enabled) return
    setLoading(true); setErro(null)
    try {
      const r = await authenticatedFetch(`/api/apontamentos?kind=${kind}&year=${year}`)
      const d = await r.json()
      if (!d.success) { setErro(d.error || 'Falha ao verificar'); return }
      setData(d)
    } catch {
      setErro('Não foi possível verificar agora')
    } finally {
      setLoading(false)
    }
  }, [authenticatedFetch, kind, year, enabled])

  useEffect(() => { recarregar() }, [recarregar])

  return { data, loading, erro, recarregar }
}

const horaCurta = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Campo_Grande' })

const ROTULO_ACAO: Record<string, string> = {
  duplicatas: 'Revisar',
  conciliacao: 'Abrir conciliação',
  lista: 'Ver na lista',
}

export function ApontamentosColuna({
  data, loading, erro, recarregar, onAcao, kind, year, onResolvido,
}: {
  data: ApontamentosData | null
  loading: boolean
  erro: string | null
  recarregar: () => void
  onAcao: (acao: string, payload?: AcaoPayload) => void
  kind: string
  year: number
  onResolvido: () => void
}) {
  const [detalhe, setDetalhe] = useState<Apontamento | null>(null)
  const pendentes = data?.itens.filter(i => i.alerta) ?? []
  const tudoOk = !!data && pendentes.length === 0

  return (
    <div className="glass-panel neu-card rounded-xl border border-border px-4 py-3 flex flex-col min-h-0 h-full">
      {/* Cabeçalho: o que é + quando foi conferido + reconferir */}
      <div className="flex items-center gap-2 shrink-0 mb-2.5">
        <span className="text-[15px] font-semibold text-foreground">Apontamentos do setor</span>
        {!!pendentes.length && (
          <span className="neu-tag bg-amber-500/10 text-amber-600 dark:text-amber-400 text-[10px] font-bold px-1.5 rounded leading-4">
            {pendentes.length}
          </span>
        )}
        <div className="flex-1" />
        {data && !loading && (
          <span className="text-[10px] text-muted-foreground tabular-nums" title={`Verificado em ${new Date(data.checkedAt).toLocaleString('pt-BR')}`}>
            {horaCurta(data.checkedAt)}
          </span>
        )}
        <button
          type="button"
          onClick={recarregar}
          disabled={loading}
          title="Verificar de novo agora"
          className="neu-convex w-6 h-6 flex items-center justify-center rounded border border-border text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-50 transition-colors"
        >
          <FiRefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin space-y-2 pr-0.5">
        {erro ? (
          <div className="text-[11px] text-red-600 dark:text-red-400 flex items-center gap-1.5">
            <FiAlertTriangle className="w-3 h-3 shrink-0" /> {erro}
          </div>
        ) : !data && loading ? (
          <div className="flex items-center justify-center py-3 text-muted-foreground">
            <FiLoader className="w-3.5 h-3.5 animate-spin" />
          </div>
        ) : tudoOk ? (
          /* Sem pendência a coluna não some — vira a confirmação de que a
             verificação RODOU. Espaço vazio seria lido como "não checou". */
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <FiCheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span>Nada pendente · {data?.itens.length ?? 0} verificações passaram</span>
          </div>
        ) : (
          pendentes.map(item => (
            /* O tooltip carrega o "por quê + o que fazer" sem gastar uma linha
               do card — a explicação completa mora na gaveta de detalhe. */
            /* Uma linha por apontamento. Antes eram três (título, detalhe,
               botões) numa caixa de 55px, e a faixa toda comia 212px da tela —
               22% da janela para seis números e três avisos, com o card de
               "Vencidos" esticado a 102px de altura para mostrar 45px de texto.
               Agora título e detalhe dividem a mesma linha e os botões só
               aparecem no hover. A explicação inteira continua no tooltip. */
            <div key={item.key} className="group neu-inset rounded-lg bg-secondary/25 px-3 py-2" title={item.explicacao || undefined}>
              <div className="flex items-baseline gap-1.5 min-w-0">
                <FiAlertTriangle className="w-3 h-3 text-amber-600 dark:text-amber-400 shrink-0 self-center" />
                <span className="text-[11px] font-medium text-foreground leading-tight shrink-0">{item.titulo}</span>
                {/* Detalhe na MESMA linha do título, truncado. O valor só entra
                    quando ainda não apareceu no texto — vários detalhes já o
                    citam, e repetir dava "…: -R$ 1.507.837,10 · -R$ 1.507.837,10". */}
                <span className="text-[10px] text-muted-foreground leading-tight truncate min-w-0">
                  {item.detalhe}
                  {item.valor != null && !item.detalhe.includes(brl(item.valor)) && (
                    <span className="text-red-600 dark:text-red-400 font-semibold"> · {brl(item.valor)}</span>
                  )}
                </span>
              </div>
              {/* Botões só no hover (e sempre visíveis no toque, que não tem
                  hover). Ocupavam uma terceira linha em cada item o tempo todo
                  para uma ação que se usa raramente. */}
              <div className="hidden group-hover:flex group-focus-within:flex items-center gap-1 mt-1">
                {/* Só oferece quando o backend tem gaveta pra essa chave. Antes
                    oferecia sempre, e os checks sem handler davam 404 no clique.
                    `!== false` mantém compatível com resposta antiga, que não
                    manda o campo. */}
                {item.detalhavel !== false && (
                <button
                  type="button"
                  onClick={() => setDetalhe(item)}
                  className="neu-convex inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                >
                  <FiList className="w-2.5 h-2.5" /> Detalhar
                </button>
                )}
                {item.acao && (
                  <button
                    type="button"
                    onClick={() => onAcao(item.acao!)}
                    className="neu-convex inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] rounded border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                  >
                    {ROTULO_ACAO[item.acao] || 'Abrir'} <FiChevronRight className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {detalhe && (
        <DetalheApontamento
          apontamento={detalhe} kind={kind} year={year}
          onClose={() => setDetalhe(null)}
          onAcao={(acao, payload) => { setDetalhe(null); onAcao(acao, payload) }}
          onResolvido={onResolvido}
        />
      )}
    </div>
  )
}

// ── Gaveta de detalhe ──────────────────────────────────────────────────────
// "2 grupos · 5 lançamentos em excesso" responde QUANTO; esta gaveta responde
// QUAIS — com id, data de criação e origem de cada linha, que é o que permite
// decidir qual cópia morre sem abrir outra tela.

interface DupLinha {
  id: number; description: string | null; reference_code: string | null
  status: string; created_at: string; conta: string | null; conciliada: boolean
}
interface DupGrupo {
  payment_date: string; amount: number; kind: string; copias: number
  lotes: number; excesso: number; conta: string | null; linhas: DupLinha[]
}
interface Coluna { campo: string; rotulo: string; tipo?: string }

const dataBR = (iso: string) => iso.split('-').reverse().join('/')

function DetalheApontamento({ apontamento, kind, year, onClose, onAcao, onResolvido }: {
  apontamento: Apontamento
  kind: string
  year: number
  onClose: () => void
  onAcao: (acao: string, payload?: AcaoPayload) => void
  onResolvido: () => void
}) {
  const { authenticatedFetch } = useAuth()
  const [dados, setDados] = useState<any>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [sel, setSel] = useState<Set<number>>(new Set())
  const [removendo, setRemovendo] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  // Dispensa de item de integridade: o diálogo pede o MOTIVO (é o que separa
  // "resolvido com justificativa" de "varrido pra baixo do tapete").
  const [motivoDialog, setMotivoDialog] = useState<{ itemKind: string; itemId: number; descricao: string } | null>(null)
  const [dispensando, setDispensando] = useState(false)

  const dispensar = async (itemKind: string, itemId: number, motivo: string) => {
    setDispensando(true); setMsg(null)
    try {
      const r = await authenticatedFetch('/api/apontamentos/dispensar', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemKind, itemId, motivo }),
      })
      const d = await r.json()
      if (!d.success) { setMsg(d.error || 'Falha ao dispensar'); return }
      setMsg('Dispensado — saiu do contador; fica aqui acinzentado e pode ser reexibido.')
      await carregar()   // a gaveta se reconfere sozinha
      onResolvido()      // e a coluna atrás dela também
    } catch {
      setMsg('Erro ao dispensar')
    } finally {
      setDispensando(false); setMotivoDialog(null)
    }
  }

  const reexibir = async (itemKind: string, itemId: number) => {
    setMsg(null)
    try {
      const r = await authenticatedFetch('/api/apontamentos/reexibir', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemKind, itemId }),
      })
      const d = await r.json()
      if (!d.success) { setMsg(d.error || 'Falha ao reexibir'); return }
      setMsg('Reexibido — volta a contar como pendência.')
      await carregar()
      onResolvido()
    } catch {
      setMsg('Erro ao reexibir')
    }
  }

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    try {
      const r = await authenticatedFetch(`/api/apontamentos/${apontamento.key}/detalhe?kind=${kind}&year=${year}`)
      const d = await r.json()
      if (!d.success) { setErro(d.error || 'Falha ao detalhar'); return }
      setDados(d)
      // Pré-seleção igual à do modal de import: as cópias (tudo menos a
      // primeira linha do grupo), nunca uma conciliada. É sugestão — quem
      // confirma é quem clica.
      if (d.formato === 'grupos') {
        const pre = new Set<number>()
        for (const g of d.grupos as DupGrupo[]) {
          g.linhas.filter(l => !l.conciliada).slice(1).forEach(l => pre.add(l.id))
        }
        setSel(pre)
      }
    } catch {
      setErro('Não foi possível carregar o detalhe')
    } finally {
      setCarregando(false)
    }
  }, [authenticatedFetch, apontamento.key, kind, year])

  useEffect(() => { carregar() }, [carregar])

  // Reusa a rota do modal de import de propósito: as guardas (nunca conciliada,
  // nunca o grupo inteiro, transação + auditoria) moram lá. Um endpoint próprio
  // aqui seria uma segunda porta pro mesmo DELETE, com metade das travas.
  const remover = async () => {
    if (!sel.size) return
    if (!confirm(`Remover ${sel.size} lançamento(s) duplicado(s)?\n\nLinhas conciliadas são preservadas, e ao menos uma cópia de cada pagamento permanece.`)) return
    setRemovendo(true); setMsg(null)
    try {
      const r = await authenticatedFetch('/api/import/duplicates/remove', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [...sel] }),
      })
      const d = await r.json()
      if (!d.success) { setMsg(d.error || 'Falha ao remover'); return }
      setSel(new Set())
      setMsg(`${d.removed} removido(s)${d.bloqueadasConciliadas ? ` · ${d.bloqueadasConciliadas} preservado(s) por estarem conciliados` : ''}.`)
      await carregar()   // a gaveta se reconfere sozinha
      onResolvido()      // e a coluna + a lista atrás dela também
    } catch {
      setMsg('Erro ao remover duplicados')
    } finally {
      setRemovendo(false)
    }
  }

  const podeRemover = dados?.formato === 'grupos' && sel.size > 0

  return (
    <SideModal
      title={apontamento.titulo}
      subtitle={apontamento.detalhe}
      icon={FiAlertTriangle}
      onClose={onClose}
      maxWidth="max-w-3xl"
      footer={dados?.formato === 'grupos' ? (
        <div className="flex items-center gap-3 w-full">
          {msg && <span className="text-xs text-muted-foreground flex-1">{msg}</span>}
          <div className="flex-1" />
          <span className="text-xs text-muted-foreground">{sel.size} selecionado(s)</span>
          <button
            type="button" onClick={remover} disabled={!podeRemover || removendo}
            className="neu-relief inline-flex items-center gap-1.5 rounded-md bg-red-600 text-white text-xs font-medium px-3 py-1.5 hover:bg-red-700 disabled:opacity-40 transition-colors"
          >
            {removendo ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiTrash2 className="w-3.5 h-3.5" />}
            Remover selecionados
          </button>
        </div>
      ) : msg ? <span className="text-xs text-muted-foreground">{msg}</span> : undefined}
    >
      {/* O "por quê" geral do check, vindo do backend — o por-sub-tipo vem
          logo abaixo, junto da tabela. */}
      {apontamento.explicacao && (
        <p className="text-xs text-muted-foreground leading-snug mb-3">{apontamento.explicacao}</p>
      )}
      {carregando ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <FiLoader className="w-5 h-5 animate-spin" />
        </div>
      ) : erro ? (
        <div className="text-sm text-red-600 dark:text-red-400">{erro}</div>
      ) : dados?.formato === 'grupos' ? (
        <div className="space-y-3">
          {(dados.grupos as DupGrupo[]).map((g, i) => (
            <div key={i} className="neu-card rounded-lg border border-border overflow-hidden">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-2 bg-secondary/40 border-b border-border">
                <span className="text-sm font-semibold text-foreground">{brl(g.amount)}</span>
                <span className="text-xs text-muted-foreground">pago em {dataBR(g.payment_date)}</span>
                {g.conta && <span className="text-xs text-muted-foreground">· {g.conta}</span>}
                <div className="flex-1" />
                <span className="text-xs text-muted-foreground">{g.copias} cópias em {g.lotes} importações</span>
                <span className="text-xs font-semibold text-red-600 dark:text-red-400">{brl(g.excesso)} em excesso</span>
              </div>
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="w-8 px-3 py-1.5"></th>
                    <th className="text-left font-medium px-3 py-1.5">#</th>
                    <th className="text-left font-medium px-3 py-1.5">Descrição</th>
                    <th className="text-left font-medium px-3 py-1.5">Origem</th>
                    <th className="text-left font-medium px-3 py-1.5">Criado em</th>
                    <th className="text-left font-medium px-3 py-1.5">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {g.linhas.map((l, j) => (
                    <tr key={l.id} className={`border-b border-border/50 last:border-0 ${sel.has(l.id) ? 'bg-red-500/5' : ''}`}>
                      <td className="px-3 py-1.5">
                        {/* Conciliada não tem checkbox, não só desmarcado: o
                            backend recusa, então oferecer o clique seria mentir
                            sobre o que é possível. */}
                        {l.conciliada ? (
                          <span className="block w-3.5 h-3.5" title="Conciliada — não pode ser removida" />
                        ) : (
                          <input
                            type="checkbox" checked={sel.has(l.id)}
                            onChange={e => setSel(s => {
                              const n = new Set(s)
                              if (e.target.checked) n.add(l.id); else n.delete(l.id)
                              return n
                            })}
                            className="w-3.5 h-3.5 accent-red-600 cursor-pointer"
                          />
                        )}
                      </td>
                      <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{l.id}</td>
                      <td className="px-3 py-1.5 text-foreground">{l.description || '—'}</td>
                      {/* Origem é o que denuncia o par: ERPMOV veio do ERP,
                          vazio veio da conciliação criando título sozinha. */}
                      <td className="px-3 py-1.5 text-muted-foreground">{l.reference_code || 'conciliação'}</td>
                      <td className="px-3 py-1.5 text-muted-foreground tabular-nums">{l.created_at}</td>
                      <td className="px-3 py-1.5">
                        {/* A primeira cópia é a que fica; conciliada nunca sai. */}
                        {l.conciliada ? (
                          <span className="neu-tag bg-primary/10 text-primary px-1.5 py-0.5 rounded text-[10px]">conciliada</span>
                        ) : j === 0 ? (
                          <span className="text-[10px] text-muted-foreground">original</span>
                        ) : (
                          <span className="neu-tag bg-amber-500/10 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded text-[10px]">cópia</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {!dados.grupos.length && <div className="text-sm text-muted-foreground">Nada a mostrar — o apontamento já foi resolvido.</div>}
        </div>
      ) : dados?.formato === 'tabela' ? (
        <div className="space-y-3">
          {/* O porquê de cada sub-tipo presente, acima da lista: quem abriu a
              gaveta precisa saber POR QUE aquilo é apontamento antes de decidir
              entre consertar e dispensar. */}
          {dados.explicacoes && !!Object.keys(dados.explicacoes).length && (
            <div className="space-y-1.5">
              {Object.entries(dados.explicacoes as Record<string, { porque: string; oQueFazer: string }>).map(([problema, e]) => (
                <div key={problema} className="neu-inset rounded-lg bg-secondary/25 px-2.5 py-1.5 text-[11px] leading-snug">
                  <span className="font-semibold text-foreground">{problema}</span>
                  <span className="text-muted-foreground"> — {e.porque} </span>
                  <span className="text-foreground">{e.oQueFazer}</span>
                </div>
              ))}
            </div>
          )}
          <div className="neu-card rounded-lg border border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground bg-secondary/40">
              <tr className="border-b border-border">
                {(dados.colunas as Coluna[]).map(c => (
                  <th key={c.campo} className="text-left font-medium px-3 py-1.5 whitespace-nowrap">{c.rotulo}</th>
                ))}
                {(apontamento.key === 'conciliacao' || apontamento.key === 'integridade') && <th className="w-10 px-3 py-1.5"></th>}
              </tr>
            </thead>
            <tbody>
              {dados.linhas.map((linha: any, i: number) => (
                <tr
                  key={i}
                  className={`border-b border-border/50 last:border-0 ${linha.dispensado ? 'opacity-50' : ''}`}
                  title={linha.dispensado
                    ? `Dispensado por ${linha.dispensado_por || '—'} em ${linha.dispensado_em || '—'}${linha.dispensa_motivo ? ` — ${linha.dispensa_motivo}` : ' (sem motivo)'}`
                    : undefined}
                >
                  {(dados.colunas as Coluna[]).map(c => {
                    const v = linha[c.campo]
                    return (
                      <td key={c.campo} className="px-3 py-1.5 text-foreground">
                        {c.tipo === 'brl' ? (v == null ? '—' : brl(v))
                          : c.tipo === 'bool' ? (v
                            ? <span className="neu-tag bg-amber-500/10 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded text-[10px]">sim</span>
                            : <span className="text-muted-foreground">não</span>)
                          : (v ?? '—')}
                      </td>
                    )
                  })}
                  {/* Conciliar exige escolher QUAL título casa com o movimento —
                      candidatos ranqueados, conferência lado a lado, opção de
                      criar título ou marcar transferência. Isso não cabe (nem
                      deve caber) numa gaveta de diagnóstico: o botão leva à tela
                      que já faz isso, com este movimento em foco. */}
                  {apontamento.key === 'conciliacao' && (
                    <td className="px-3 py-1.5">
                      <button
                        type="button"
                        title="Abrir este movimento na conciliação"
                        onClick={() => onAcao('conciliacao', { movimentoId: linha.id })}
                        className="neu-convex inline-flex items-center justify-center w-6 h-6 rounded border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                      >
                        <FiExternalLink className="w-3 h-3" />
                      </button>
                    </td>
                  )}
                  {apontamento.key === 'integridade' && (
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1 justify-end">
                        {/* Corrigenda: só o "conciliado sem vínculo" tem um lugar
                            certo de conserto (a conciliação, com o movimento em
                            foco — e na aba CONCILIADOS, que é onde ele vive).
                            Os outros sub-tipos se consertam editando o
                            lançamento na lista. */}
                        {!linha.dispensado && linha.problema === 'conciliado sem vínculo' && (
                          <button
                            type="button"
                            title="Abrir este movimento na conciliação (aba Conciliados) para reabrir ou amarrar"
                            onClick={() => onAcao('conciliacao', { movimentoId: linha.id, status: 'conciliado' })}
                            className="neu-convex inline-flex items-center justify-center w-6 h-6 rounded border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                          >
                            <FiExternalLink className="w-3 h-3" />
                          </button>
                        )}
                        {!linha.dispensado ? (
                          <button
                            type="button"
                            title="Dispensar com motivo — sai do contador, fica aqui acinzentado e é reversível"
                            onClick={() => setMotivoDialog({ itemKind: linha.item_kind, itemId: linha.id, descricao: linha.description || `#${linha.id}` })}
                            className="neu-convex inline-flex items-center justify-center w-6 h-6 rounded border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                          >
                            <FiEyeOff className="w-3 h-3" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            title="Reexibir — volta a contar como pendência"
                            onClick={() => reexibir(linha.item_kind, linha.id)}
                            className="neu-convex inline-flex items-center justify-center w-6 h-6 rounded border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                          >
                            <FiEye className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {!dados.linhas.length && (
                <tr><td colSpan={dados.colunas.length + (apontamento.key === 'conciliacao' || apontamento.key === 'integridade' ? 1 : 0)} className="px-3 py-4 text-center text-muted-foreground">Nada a mostrar — o apontamento já foi resolvido.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      ) : null}
      {motivoDialog && (
        <DialogoMotivo
          descricao={motivoDialog.descricao}
          busy={dispensando}
          onCancel={() => setMotivoDialog(null)}
          onConfirm={motivo => dispensar(motivoDialog.itemKind, motivoDialog.itemId, motivo)}
        />
      )}
    </SideModal>
  )
}

// ── Diálogo de motivo da dispensa ──────────────────────────────────────────
// Não é o ConfirmDialog: precisa de um campo de texto, e estender o compartilhado
// com input mexeria nos 3 usos que devem ficar idênticos. Herda dele os dois
// truques de conviver com o SideModal: portal (a animação do drawer cria bloco
// de contenção que recortaria um `fixed` filho) e Esc em fase de CAPTURA com
// stopImmediatePropagation (senão o Esc fecha o drawer inteiro junto).
function DialogoMotivo({ descricao, busy, onConfirm, onCancel }: {
  descricao: string
  busy: boolean
  onConfirm: (motivo: string) => void
  onCancel: () => void
}) {
  const [motivo, setMotivo] = useState('')
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopImmediatePropagation()
      e.preventDefault()
      onCancel()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onCancel])

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4 glass-overlay" style={{ zIndex: 100001 }}>
      <div className="glass-panel rounded-xl max-w-md w-full border border-border p-5">
        <div className="flex items-center gap-2 mb-1">
          <FiEyeOff className="w-4 h-4 text-muted-foreground shrink-0" />
          <h3 className="text-sm font-semibold text-foreground">Dispensar apontamento</h3>
        </div>
        <p className="text-xs text-muted-foreground leading-snug mb-3">
          "{descricao}" sai do contador, mas continua na gaveta acinzentado — e pode ser reexibido a qualquer momento.
        </p>
        <textarea
          autoFocus
          value={motivo}
          onChange={e => setMotivo(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Motivo (recomendado): por que este item não é um problema?"
          className="w-full text-xs rounded-lg border border-border bg-background text-foreground p-2 mb-3 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <div className="flex gap-2 justify-end">
          <button
            type="button" onClick={onCancel}
            className="neu-relief px-3 py-1.5 text-xs font-medium rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button" onClick={() => onConfirm(motivo)} disabled={busy}
            className="neu-relief inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 transition-colors"
          >
            {busy ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiEyeOff className="w-3.5 h-3.5" />}
            Dispensar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

// Faixa de conciliação por mês — vive na ABA DE CONCILIAÇÃO (movida em 27/07:
// estava sobre a lista de lançamentos, que não é o assunto dela).
//
// O percentual sozinho engana, então cada mês carrega o contexto:
//  ok          verde     — extrato importado e todo conciliado
//  incompleto  âmbar     — tudo conciliado, mas sobre uma fração dos pagamentos
//                          do mês (o "100%" de janeiro: 5 movimentos, 732 pagos)
//  parcial     âmbar     — sobrou pendência na fila
//  sem_extrato vermelho  — mês teve pagamentos e NENHUM extrato (fev–abr)
//  vazio       cinza     — mês sem pagamento e sem extrato: nada a cobrar
const ESTILO_MES: Record<string, string> = {
  ok: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  incompleto: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  parcial: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  sem_extrato: 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400',
  vazio: 'border-dashed border-border text-muted-foreground/60',
}

export function FaixaConciliacao({
  data, onAcao,
}: {
  data: ApontamentosData | null
  onAcao: (acao: string) => void
}) {
  if (!data?.conciliacao) return null
  const visiveis = data.conciliacao.meses.filter(m => m.estado !== 'futuro')
  if (!visiveis.length) return null

  const semExtrato = visiveis.filter(m => m.estado === 'sem_extrato')
  const incompletos = visiveis.filter(m => m.estado === 'incompleto')

  const rotuloMes = (m: MesConciliacao) => {
    if (m.estado === 'sem_extrato' || m.estado === 'vazio') return '—'
    return `${m.pct}%`
  }
  // Verde com linha ignorada não pode ser verde MUDO: ignorar é legítimo (lote
  // já lançado), mas também é onde alguém esconderia trabalho não feito. O mês
  // fecha, e o número aparece — cor diz "sem pendência", texto diz o que houve.
  const sufixoIgnorados = (m: MesConciliacao) =>
    m.ignorados > 0 ? ` · ${m.ignorados} ignorado(s): linha de lote cujos títulos já estão lançados` : ''
  const comIgnorados = visiveis.filter(m => m.ignorados > 0)
  const tituloMes = (m: MesConciliacao) => {
    const nome = MESES[m.mes - 1]
    switch (m.estado) {
      case 'sem_extrato':
        return `${nome}: NENHUM extrato importado, e o mês tem ${m.pagos} lançamento(s) pago(s) — nada foi conferido contra o banco`
      case 'vazio':
        return `${nome}: sem extrato e sem pagamentos`
      case 'incompleto':
        return `${nome}: ${m.tratados} de ${m.total} movimentos tratados, mas o mês tem ${m.pagos} lançamento(s) pago(s) — o extrato cobre só uma fração${sufixoIgnorados(m)}`
      case 'parcial':
        return `${nome}: ${m.tratados} de ${m.total} tratados · ${m.total - m.tratados} pendente(s)${sufixoIgnorados(m)}`
      default:
        return `${nome}: ${m.conciliados} de ${m.total} movimentos conciliados · ${m.pagos} pago(s) no mês${sufixoIgnorados(m)}`
    }
  }

  return (
    // PLANOS (21/10/2026): a faixa dos meses era uma linha solta de 10–11px
    // sobre o chão; agora é uma faixa de papel com rótulo, como as barras.
    <div className="pln-barra shrink-0">
      <span className="pln-barra-rot shrink-0">Conciliação {data.conciliacao.year}</span>
      <div className="flex items-center gap-1 flex-wrap min-w-0">
        {visiveis.map(m => {
          const clicavel = m.estado === 'parcial'
          return (
            <button
              key={m.mes}
              type="button"
              title={tituloMes(m)}
              onClick={() => clicavel && onAcao('conciliacao')}
              className={`rounded-lg border px-2.5 py-1 text-xs leading-4 whitespace-nowrap transition-colors ${
                ESTILO_MES[m.estado] || ESTILO_MES.vazio} ${clicavel ? 'cursor-pointer hover:border-primary/50' : 'cursor-default'}`}
            >
              <span className="uppercase font-medium">{MESES[m.mes - 1]}</span>
              <span className="tabular-nums ml-1">{rotuloMes(m)}</span>
            </button>
          )
        })}
      </div>
      {/* O aviso é escrito por extenso: a cor sozinha some pra quem não sabe o
          que ela significa, e este é o achado mais caro da faixa. */}
      {!!semExtrato.length && (
        <span className="text-[10px] text-red-600 dark:text-red-400 font-medium shrink-0">
          {semExtrato.map(m => MESES[m.mes - 1]).join(', ')} sem extrato importado
        </span>
      )}
      {!!incompletos.length && (
        <span className="text-[10px] text-amber-600 dark:text-amber-400 shrink-0">
          {incompletos.map(m => MESES[m.mes - 1]).join(', ')} com extrato parcial
        </span>
      )}
      {!!comIgnorados.length && (
        <span className="text-[10px] text-muted-foreground shrink-0"
          title="Linhas do extrato marcadas como Ignorado: são lotes (boleto, borderô) cujos títulos já estão lançados e pagos um a um no ERP. Contam como tratadas — o saldo do sistema já inclui esse dinheiro.">
          {comIgnorados.reduce((s, m) => s + m.ignorados, 0)} linha(s) ignorada(s) em {comIgnorados.map(m => MESES[m.mes - 1]).join(', ')}
        </span>
      )}
    </div>
  )
}
